import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LoadingButton, PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { OfficeRouteFields, Section } from '../components/ProcessFilters';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

/** Exportar clientes y préstamos de una ruta a un archivo CSV. */
const ClientLoanExport = () => {
  const t = useT();
  const notify = useNotify();
  const { canExport } = useResourcePermissions('/processes/export');
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setPageTitle('dataExport.title');
    (async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(data?.data ?? []);
      } catch (error) {
        notify.error(t('dataExport.loadError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRouteId('');
    if (!officeId) {
      setOfficeRoutes([]);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`offices/${officeId}/routes`);
        setOfficeRoutes(Array.isArray(data) ? data : []);
      } catch (error) {
        setOfficeRoutes([]);
        notify.error(t('dataExport.loadError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeId]);

  const handleExport = async () => {
    if (!canExport || !routeId) return;
    setExporting(true);
    try {
      const response = await axios.post('/exports/clients-loans', { routeId }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = t('dataExport.fileName');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notify.success(t('dataExport.done'));
    } catch (error) {
      notify.error(t('dataExport.exportError'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <HeaderResponsive>
      <PageHeader title={t('dataExport.title')} description={t('dataExport.subtitle')} helpAction={<ScreenTourButton />} />
      <Box sx={{ maxWidth: 880 }}>
        <Section title={t('dataExport.sectionTitle')}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
            <OfficeRouteFields
              idPrefix="export"
              tourPrefix="export"
              offices={offices}
              routes={officeRoutes}
              officeId={officeId}
              routeId={routeId}
              onOfficeChange={setOfficeId}
              onRouteChange={setRouteId}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} data-tour="export-hint">
            {t('dataExport.hint')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {canExport ? (
              <LoadingButton
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                loading={exporting}
                disabled={!routeId}
                onClick={() => void handleExport()}
                data-tour="export-csv"
              >
                {t('dataExport.exportCsv')}
              </LoadingButton>
            ) : (
              <PermissionButton
                allowed={false}
                deniedTooltip={t('dataExport.noPermissionExport')}
                variant="contained"
                data-tour="export-csv"
                startIcon={<FileDownloadOutlinedIcon />}
              >
                {t('dataExport.exportCsv')}
              </PermissionButton>
            )}
          </Box>
        </Section>
      </Box>
    </HeaderResponsive>
  );
};

export default ClientLoanExport;
